/**
 * Identity and offices (WP0-AUTH): sign-in, two-step sign-in, the lock
 * screen, the Users, Roles, Offices, Workstations and My sign-in pages, and
 * the C2G Admin console and set-password page for Computers2Go support.
 * The session provider, sign-in gate and office selector the shell uses are
 * exported from `./index.ts`.
 */
import { defineManifest } from '@velora/contracts';
import { defineWebModule } from '../../app/module.js';

export default defineWebModule({
  manifest: defineManifest({
    id: 'identity',
    title: 'Identity and offices',
    version: '0.1.0',
    owner: 'WP0-AUTH',
    entitlementKey: null,
    permissions: [
      'users.view',
      'users.manage',
      'roles.manage',
      'offices.manage',
      'devices.manage',
      'support.impersonate',
    ],
    routes: [
      { id: 'sign-in', path: '/login', title: 'Sign in', officeScope: 'practice' },
      {
        id: 'support-sign-in',
        path: '/support/sign-in',
        title: 'Support sign-in',
        officeScope: 'practice',
      },
      {
        id: 'set-password',
        path: '/support/set-password',
        title: 'Set your password',
        officeScope: 'practice',
      },
      {
        // W12: a practice's staff invite (Settings > Users). Bare page, same
        // page as the support set-password link.
        id: 'accept-invite',
        path: '/auth/invite',
        title: 'Set up your account',
        officeScope: 'practice',
      },
      {
        // Computers2Go support accounts only (the support-only C2G Support
        // role is the only holder of support.impersonate). Bare page.
        id: 'c2g-admin',
        path: '/support/admin',
        title: 'C2G Admin',
        officeScope: 'practice',
        permission: 'support.impersonate',
      },
      {
        id: 'users',
        path: '/settings/users',
        title: 'Users',
        officeScope: 'practice',
        permission: 'users.view',
      },
      {
        id: 'roles',
        path: '/settings/roles',
        title: 'Roles',
        officeScope: 'practice',
        permission: 'users.view',
      },
      {
        // Two-step sign-in methods per role (2026-11-02).
        id: 'security',
        path: '/settings/security',
        title: 'Security',
        officeScope: 'practice',
        permission: 'users.view',
      },
      { id: 'offices', path: '/settings/offices', title: 'Offices', officeScope: 'practice' },
      {
        id: 'workstations',
        path: '/settings/workstations',
        title: 'Workstations',
        officeScope: 'practice',
        permission: 'devices.manage',
      },
      {
        id: 'my-sign-in',
        path: '/settings/my-sign-in',
        title: 'My sign-in',
        officeScope: 'practice',
      },
    ],
    nav: [
      {
        label: 'Users',
        group: 'settings',
        routeId: 'users',
        icon: 'users',
        order: 910,
        permission: 'users.view',
      },
      {
        label: 'Roles',
        group: 'settings',
        routeId: 'roles',
        icon: 'shield',
        order: 920,
        permission: 'users.view',
      },
      {
        label: 'Security',
        group: 'settings',
        routeId: 'security',
        icon: 'lock',
        order: 925,
        permission: 'users.view',
      },
      {
        label: 'Offices',
        group: 'settings',
        routeId: 'offices',
        icon: 'building-2',
        order: 930,
        permission: 'offices.manage',
      },
      {
        label: 'Workstations',
        group: 'settings',
        routeId: 'workstations',
        icon: 'monitor',
        order: 940,
        permission: 'devices.manage',
      },
      {
        label: 'C2G Admin',
        group: 'settings',
        routeId: 'c2g-admin',
        icon: 'lock',
        order: 990,
        permission: 'support.impersonate',
      },
      {
        label: 'My sign-in',
        group: 'settings',
        routeId: 'my-sign-in',
        icon: 'key-round',
        order: 950,
      },
    ],
    commands: [
      {
        id: 'identity.users',
        title: 'Manage users',
        keywords: ['staff', 'people', 'access'],
        routeId: 'users',
        permission: 'users.view',
      },
      {
        id: 'identity.roles',
        title: 'Edit roles and permissions',
        keywords: ['permissions', 'access'],
        routeId: 'roles',
        permission: 'users.view',
      },
      {
        id: 'identity.security',
        title: 'Two-step sign-in methods (text, email, authenticator)',
        keywords: ['security', 'mfa', 'two-step', 'sms', 'text message', 'email code'],
        routeId: 'security',
        permission: 'users.view',
      },
      {
        id: 'identity.offices',
        title: 'Offices',
        keywords: ['locations', 'add office', 'new office'],
        routeId: 'offices',
      },
      {
        id: 'identity.workstations',
        title: 'Set up this workstation',
        keywords: ['device', 'computer', 'idle lock'],
        routeId: 'workstations',
        permission: 'devices.manage',
      },
      {
        id: 'identity.my-sign-in',
        title: 'My sign-in, PIN and two-step',
        keywords: ['password', 'pin', 'mfa', 'authenticator'],
        routeId: 'my-sign-in',
      },
    ],
  }),
  pages: {
    'sign-in': () => import('./pages/SignInPage.js'),
    'support-sign-in': () => import('./pages/SupportSignInPage.js'),
    'set-password': () => import('./pages/SetPasswordPage.js'),
    'accept-invite': () => import('./pages/SetPasswordPage.js'),
    'c2g-admin': () => import('./pages/SupportAdminPage.js'),
    users: () => import('./pages/UsersPage.js'),
    roles: () => import('./pages/RolesPage.js'),
    security: () => import('./pages/SecurityPolicyPage.js'),
    offices: () => import('./pages/OfficesPage.js'),
    workstations: () => import('./pages/DevicesPage.js'),
    'my-sign-in': () => import('./pages/SecurityPage.js'),
  },
});
